import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { SwatchKind } from "@/components/primitives";
import { readPrefs } from "@/lib/notificationPrefs";
import { registerChannel, unregisterChannel } from "@/lib/realtimeChannels";

export type NotificationKind =
  | "new_follower"
  | "new_note"
  | "new_love"
  | "grail_match"
  | "new_message"
  | "verification_complete"
  | "post_removed"
  | "post_reinstated"
  | "verified_approved"
  | "moderator_eligible"
  | "moderator_invite";

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  createdAt: string;
  read: boolean;
  title: string;
  preview: string | null;
  actorHandle: string | null;
  actorAvatar: SwatchKind | null;
  href: string;
  scanTitle?: string | null;
}

function hrefFor(type: string, data: Record<string, unknown>, actorHandle: string | null): string {
  switch (type) {
    // Profile is routed by handle, not UUID. Use the actor's resolved handle
    // (looked up in profiles_public) rather than data.follower_id, which is
    // the raw auth user id and won't match :handle.
    case "new_follower":        return actorHandle ? `/profile/${actorHandle}` : "/feed";
    case "new_note":
    case "new_love":
    case "verification_complete":
    case "post_removed":
    case "post_reinstated":     return data.scan_id ? `/post/${data.scan_id}` : "/feed";
    case "grail_match":         return "/grails?focus=matches";
    case "new_message":         return data.conversation_id ? `/messages/${data.conversation_id}` : "/messages";
    case "verified_approved":   return "/you";
    case "moderator_eligible":  return "/settings/moderator-application";
    case "moderator_invite":
      return data.invite_id ? `/moderator-invite/${data.invite_id}` : "/you";
    // Find of the Week — voting CTA goes to the ballot page; the winner
    // announcement deep-links to the winning post (now publicly identified).
    case "weekly_feature_voting_open": return "/find-of-the-week";
    case "weekly_feature_winner":
      return data.winning_scan_id
        ? `/post/${data.winning_scan_id}`
        : "/find-of-the-week";
    default:                    return "/notifications";
  }
}

// Map notification.type to the pref key used in notificationPrefs.ts
// (grail / message / comment / reaction remain for backward compat).
function prefKind(type: string): string {
  switch (type) {
    case "grail_match":   return "grail";
    case "new_message":   return "message";
    case "new_note":      return "comment";
    case "new_love":      return "reaction";
    default:              return type;
  }
}

export function useNotifications() {
  const { user } = useAuth();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setItems([]); setLoading(false); return; }
    setLoading(true);

    const { data: rows } = await supabase
      .from("notifications")
      .select("id, actor_id, type, title, body, data, read, created_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (!rows || rows.length === 0) { setItems([]); setLoading(false); return; }

    // Batch-resolve actor profiles.
    const actorIds = Array.from(new Set(rows.map((r) => r.actor_id).filter(Boolean)));
    let profileMap = new Map<string, { handle: string; avatar: string }>();
    if (actorIds.length) {
      const { data: profs } = await (supabase as any)
        .from("profiles_public")
        .select("id, handle, avatar")
        .in("id", actorIds);
      profileMap = new Map(
        ((profs ?? []) as Array<{ id: string; handle: string; avatar: string }>)
          .map((p) => [p.id, p] as const)
      );
    }

    const prefs = readPrefs(user.id);

    const out: NotificationItem[] = rows
      .map((r) => {
        const pk = prefKind(r.type);
        if (prefs[pk] === false) return null;
        const actor = r.actor_id ? profileMap.get(r.actor_id) : undefined;
        const data = (r.data ?? {}) as Record<string, unknown>;
        return {
          id: r.id,
          kind: r.type as NotificationKind,
          createdAt: r.created_at,
          read: !!r.read,
          title: r.title,
          preview: r.body ?? null,
          actorHandle: actor?.handle ?? null,
          actorAvatar: (actor?.avatar as SwatchKind) ?? null,
          href: hrefFor(r.type, data, actor?.handle ?? null),
          scanTitle: (data.scan_title as string) ?? null,
        } satisfies NotificationItem;
      })
      .filter((x): x is NotificationItem => x !== null);

    setItems(out);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
    if (!user) return;
    const onPrefs = () => { void load(); };
    window.addEventListener("fig-notif-prefs", onPrefs);
    const key = `notifs-${user.id}-${Math.random().toString(36).slice(2, 8)}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
          () => { void load(); },
        )
        .subscribe()
    );
    return () => {
      window.removeEventListener("fig-notif-prefs", onPrefs);
      unregisterChannel(key);
    };
  }, [user, load]);

  const unseenCount = items.filter((i) => !i.read).length;

  const markAllSeen = useCallback(async () => {
    if (!user) return;
    await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", user.id)
      .eq("read", false);
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
  }, [user]);

  return { items, loading, unseenCount, refresh: load, markAllSeen };
}

// Lightweight unseen count for the bell badge — just counts unread rows.
export function useUnseenGrailMatches(onNew?: (row: { id: string; grail_id: string }) => void) {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) { setCount(0); return; }
    const { count: n } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("read", false)
      .eq("type", "grail_match");
    setCount(n ?? 0);
  }, [user]);

  useEffect(() => {
    void refresh();
    if (!user) return;
    const key = `grail-unseen-${user.id}-${Math.random().toString(36).slice(2, 8)}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
          (payload) => {
            const row = payload.new as { type: string; data?: { grail_id?: string; scan_id?: string } };
            if (row.type === "grail_match") {
              setCount((c) => c + 1);
              if (onNew) onNew({ id: (payload.new as { id: string }).id, grail_id: row.data?.grail_id ?? "" });
            }
          },
        )
        .subscribe()
    );
    return () => { unregisterChannel(key); };
  }, [user, refresh, onNew]);

  return { count, refresh };
}
